// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * palette.ts — every colour that carries meaning, in one place.
 *
 * This file exists because of principle #22 and the Scrapwall bug behind it: a
 * game piece drawn in the right cell at the right size, in a colour 1.14:1
 * against the board, is INVISIBLE — and it passes every geometry gate the
 * factory runs, because it is on-screen, correctly sized and non-overlapping.
 * On a deliberately cold, moody palette "screenshot it and look" reads invisible
 * as atmosphere. So no renderer in this game is allowed an inline colour
 * literal: they all live here, where `tests/contrast.test.ts` can measure them.
 *
 * The contrast model has two tiers, because Lastlight draws two different kinds
 * of thing and holding both to the same rule would be wrong:
 *
 *   TERRAIN is the play surface itself. It is not a mark ON a surface, so 3:1
 *   against the map background is not the meaningful question. What matters is
 *   that the four terrains are told APART — including in greyscale, for
 *   colour-blind players — so they are laid out as a deliberate luminance
 *   ladder (water darkest, snow lightest) and pinned pairwise.
 *
 *   MARKS — buildings, the generator, scouts, the warmth ring — are drawn ON
 *   terrain and carry meaning, so they take the full WCAG 1.4.11 3:1 floor
 *   against EVERY terrain they can ever sit on, lit and frozen alike. They are
 *   all bright against a dark map, which is what makes that achievable at all.
 */

/** sRGB hex -> WCAG relative luminance. Exported so the tests measure the real thing. */
export function luminance(hex: string): number {
  const h = hex.replace('#', '');
  const ch = [0, 2, 4].map((i) => {
    const v = parseInt(h.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

/** WCAG contrast ratio between two sRGB hex colours. */
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

function channels(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function hex(r: number, g: number, b: number): string {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** Linear blend from `a` to `b`. `t` 0 = all a, 1 = all b. */
export function mix(a: string, b: string, t: number): string {
  const [ar, ag, ab] = channels(a);
  const [br, bg, bb] = channels(b);
  return hex(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}

/** The colour a tile takes once the warmth has left it: colder, flatter, deader. */
export function freeze(base: string): string {
  return mix(base, FROST_SINK, 0.5);
}

/** What a frozen tile is pulled toward — the colour of dead ground. */
export const FROST_SINK = '#1b2a44';

export const PALETTE = {
  /** Behind and around the map. */
  ground: '#0d1626',
  /** The hairline between hexes. */
  gridline: '#1b2740',

  // ── marks: bright, >=3:1 against every terrain below ────────────────────
  /** Hearth-home. Shelters people — the warmest thing you can build. */
  home: '#ffcf8a',
  /** Gathering hut. */
  hut: '#86e5b4',
  /** Workshop. */
  workshop: '#d9ccff',
  /** The generator's body. */
  generator: '#ffc074',
  /** The last light burning in it. */
  core: '#fff1d6',
  /** A scout, out in the fog. */
  scout: '#8ad4ff',
  /** The warmth boundary. Drawn over a dark casing so it reads on any tile. */
  ring: '#ffab52',
  /** The casing stroked under the ring, so the ring never sits colour-on-colour. */
  ringCasing: '#0a111e',
  /** A cache waiting in the fog, once revealed. */
  cache: '#ffd76e',
  /**
   * The ice crystals drawn over a tile the warmth has left.
   *
   * This overlay, not the fill colour, is what actually tells a player a tile
   * is dead — and that is a measured decision, not a stylistic one. No single
   * tint can darken the darkest terrain and the lightest terrain by the same
   * visible amount, so a "frozen" state carried by fill alone is necessarily
   * invisible on one end of the ladder (measured: frozen forest came out at
   * 1.03:1 against lit forest — literally no change). Making the marker a MARK
   * means it takes the same 3:1 floor as everything else and works on all four.
   */
  frost: '#c3dcf2',

  // ── surfaces ─────────────────────────────────────────────────────────────
  /** Unexplored. Nothing meaningful is drawn on it except the scout's thread. */
  fog: '#162033',

  // ── ui ───────────────────────────────────────────────────────────────────
  text: '#eaf0fa',
  textDim: '#9fb0c9',
  panel: '#131d30',
  panelEdge: '#26344f',
  danger: '#ff8a7a',
  good: '#86e5b4',
} as const;

/**
 * The four terrains, as a deliberate luminance ladder so they survive being
 * read in greyscale: water darkest, then forest, then seam, then snow.
 * `tests/contrast.test.ts` pins both the ordering and the pairwise separation.
 */
export const TERRAIN = {
  water: '#0b2c4c',
  forest: '#16452c',
  seam: '#564470',
  snow: '#57687e',
} as const;

export type TerrainName = keyof typeof TERRAIN;

export const TERRAIN_NAMES: readonly TerrainName[] = ['snow', 'forest', 'water', 'seam'];

/** Precomputed so the tests enumerate exactly what the renderer paints. */
export const TERRAIN_FROZEN: Record<TerrainName, string> = {
  water: freeze(TERRAIN.water),
  forest: freeze(TERRAIN.forest),
  seam: freeze(TERRAIN.seam),
  snow: freeze(TERRAIN.snow),
};

/** Every surface a mark can be drawn on. The contrast test walks this exhaustively. */
export const ALL_SURFACES: readonly string[] = [
  ...Object.values(TERRAIN),
  ...Object.values(TERRAIN_FROZEN),
];

/** Every mark drawn onto a tile. Held to 3:1 against all of ALL_SURFACES. */
export const ALL_MARKS: ReadonlyArray<readonly [string, string]> = [
  ['a hearth-home', PALETTE.home],
  ['a gathering hut', PALETTE.hut],
  ['a workshop', PALETTE.workshop],
  ['the generator', PALETTE.generator],
  ['the last light', PALETTE.core],
  ['a scout', PALETTE.scout],
  ['a revealed cache', PALETTE.cache],
  ['the frost on a dead tile', PALETTE.frost],
];
